import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCollectionProgress } from '../../services/collectionsService';
import AppPromotionCard from '../../components/AppPromotionCard/AppPromotionCard';
import ProgressiveCoinReveal from '../../components/ProgressiveCoinReveal/ProgressiveCoinReveal';
import { CoinCelebration } from '../../components/CoinCelebration/CoinCelebration';
import styles from './CollectionDetail.module.css';
import CoinMedia from '../../components/CoinMedia/CoinMedia';
import { resolveCoinMedia } from '../../utils/coinMedia';
import { SeasonalBanner, seasonalProgressCopy } from '../../components/SeasonalAvailability/SeasonalAvailability';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { completionNumbers, progressLabel, requirementSentence, secondaryProgressLabel, unlockFraction } from '@aluvo/constants/src/collectionCompletion';
import { CollectionCollectorStats } from '../../components/CollectorStats/CollectorStats';

const COIN_SIZE = {
    collectionDetail: 220,
    collectionList: 80,
};

export default function CollectionDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [celebrating, setCelebrating] = useState(false);

    useEffect(() => {
        loadCollection();
    }, [slug]);

    // Seasonal analytics: once per collection per page session, never per render.
    useEffect(() => {
        if (!collection?.isSeasonal) return;
        const props = { collection_id: collection.id, availability_status: collection.availabilityStatus };
        trackEvent(ANALYTICS_EVENTS.SEASONAL_COLLECTION_VIEWED, props, { onceKey: collection.id });
        if (collection.availabilityStatus === 'ended') {
            trackEvent(ANALYTICS_EVENTS.SEASONAL_COLLECTION_EXPIRED_VIEW, props, { onceKey: collection.id });
        }
    }, [collection]);

    async function loadCollection() {
        try {
            setLoading(true);
            const data = await getCollectionProgress();
            const found = data.find((c) => c.slug === slug);
            setCollection(found || null);
        } catch (err) {
            console.error('Failed to load collection:', err);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.loadingCoinPlaceholder}>
                        <div className={styles.coinSilhouettePlaceholder} />
                    </div>
                    <p className={styles.loading}>Loading collection...</p>
                </div>
            </div>
        );
    }

    if (!collection) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <p className={styles.errorText}>Collection not found.</p>
                </div>
            </div>
        );
    }

    const rewardMedia = resolveCoinMedia(collection.reward);
    // Progress toward UNLOCKING (the threshold for "any N of M" collections).
    const progressPercent = unlockFraction(collection) * 100;
    const { towardRequired, required, isMinimum } = completionNumbers(collection);
    const nextTitle = collection.items?.find((i) => !i.verified)?.title;

    // Tighter, more direct description copy
    const hasTV    = collection.items?.some((i) => i.kind === 'tv');
    const hasMovie = collection.items?.some((i) => i.kind === 'movie');
    const titleWord = hasTV && hasMovie ? 'titles' : 'films';
    const descriptionCopy = requirementSentence(collection, { titleWord, rewardName: collection.reward?.name });
    // Seasonal reward not earnable right now (upcoming / ended, not owned).
    const seasonalCopy = seasonalProgressCopy(collection);

    return (
        <div className={styles.page}>
            {/* ── Collection unlock celebration ── */}
            {celebrating && (
                <CoinCelebration
                    coinImageUrl={collection.reward.coinImageUrl}
                    coinMedia={collection.reward.media}
                    coinLabel={collection.reward.name}
                    subtitle="🎉 Collection Complete!"
                    onComplete={() => setCelebrating(false)}
                />
            )}

            <div className={styles.container}>
                <div className={styles.header}>
                    <h1>{collection.name}</h1>
                    <p className={styles.description}>{descriptionCopy}</p>
                    <SeasonalBanner collection={collection} />

                    <div className={styles.rewardBox}>
                        <span className={styles.rewardTitle}>
                            {rewardMedia.type === 'ANIMATED' ? '💎 Unlocks Signature Coin' : 'Unlocks Legendary Coin'}
                        </span>
                        <div className={styles.rewardContent}>
                            {/* A completed Signature reward loops here; until then the
                                static poster is revealed progressively like any coin. */}
                            {rewardMedia.type === 'ANIMATED' && collection.completed && !celebrating ? (
                                <div className={styles.coinHeroWrapper}>
                                    <CoinMedia
                                        coin={collection.reward}
                                        animate
                                        alt={collection.reward.name}
                                        imgProps={{ width: COIN_SIZE.collectionDetail, height: COIN_SIZE.collectionDetail }}
                                        className={styles.signatureHero}
                                    />
                                </div>
                            ) : rewardMedia.posterUrl && (
                                <div className={styles.coinHeroWrapper}>
                                    <ProgressiveCoinReveal
                                        coinImageUrl={rewardMedia.posterUrl}
                                        coinName={collection.reward.name}
                                        verified={towardRequired}
                                        required={required}
                                        size={COIN_SIZE.collectionDetail}
                                        bgColor="var(--color-bg-secondary, #1a1a1a)"
                                        nextTitle={nextTitle}
                                        onTap={() => {/* zoom handler if needed */}}
                                        /* A full reveal of an upcoming/ended reward the user does not own is not a win. */
                                        onComplete={() => { if (collection.completed) setCelebrating(true); }}
                                    />
                                    {!collection.completed && (
                                        <div className={styles.tapHint}>
                                            <i className="ti ti-zoom-in" aria-hidden="true" />
                                            <span className={styles.tapHintText}>Tap to enlarge</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            <h2 className={styles.rewardName}>💎 {collection.reward.name}</h2>
                            <CollectionCollectorStats collection={collection} />
                        </div>
                        {!collection.completed && (
                            <p className={styles.rewardHint}>
                                {seasonalCopy || (collection.progress === 0
                                    ? 'Verify your first title to start revealing the coin'
                                    : `${collection.remaining} more ${collection.remaining === 1 ? 'title' : 'titles'} to fully reveal`)}
                            </p>
                        )}
                    </div>

                    <div className={styles.progressContainer}>
                        <div className={styles.progressTextRow}>
                            <span className={styles.progressText}>
                                {progressLabel(collection)}
                            </span>
                            {!collection.completed && (
                                <span className={styles.remainingText}>
                                    {seasonalCopy || `${collection.remaining} more to unlock`}
                                </span>
                            )}
                            {collection.completed && (
                                <span className={styles.completedText}>✓ Collection Complete</span>
                            )}
                        </div>
                        {isMinimum && (
                            <div className={styles.secondaryProgress}>{secondaryProgressLabel(collection)}</div>
                        )}
                        <div className={styles.progressBarBg}>
                            <div
                                className={`${styles.progressBarFill} ${collection.completed ? styles.completed : ''}`}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                    </div>

                    {!collection.completed && collection.remaining === 1 && !seasonalCopy && (
                        <AppPromotionCard
                            variant="collection"
                            rewardCoinName={collection.reward?.name}
                            compact
                        />
                    )}
                </div>

                <div className={styles.itemsGrid}>
                    {collection.items.map((item, idx) => (
                        <div
                            key={idx}
                            className={styles.itemCard}
                            onClick={() => {
                                if (item.tmdbId) {
                                    navigate(`/movie/${item.kind}/${item.tmdbId}`);
                                }
                            }}
                        >
                            {item.posterUrl && (
                                <img src={item.posterUrl} alt={item.title} className={styles.itemPoster} />
                            )}
                            <div className={styles.itemInfo}>
                                <h3 className={styles.itemTitle}>{item.title || 'Unknown Title'}</h3>
                                {item.requirementType === 'tv_season' && item.seasonNumber != null && (
                                    <span className={styles.seasonLabel}>Season {item.seasonNumber}</span>
                                )}
                                {item.verified && (
                                    <div className={styles.verifiedRow}>
                                        <span className={styles.verifiedText}>✓ Verified</span>
                                        {item.coinTier && (
                                            <span className={styles.coinTier}>{item.coinTier}</span>
                                        )}
                                    </div>
                                )}
                                {!item.verified && (
                                    <span className={styles.notVerified}>
                                        {item.requirementType === 'tv_season' && item.seasonNumber != null
                                            ? `Complete Season ${item.seasonNumber} to verify`
                                            : 'Not verified yet'}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}